import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/QrPrintDesigner.css";
import XIcon from "../icons/XIcon.js";
import Uploadbutton from "../icons/BusinessHub/Uploadbutton.js";
import CustomDropdown from "./StatusDropdown.js";
import ArrowDownIcon from "../icons/BusinessHub/ArrowDownIcon.js";

const DEFAULT_PRINT_DESIGN = {
  showLogo: true,
  logoSrc: "",
  logoPosition: "right",
  logoSizeCm: 0.9,
  logoShape: "rounded",
  showLabel: false,
  showProductName: true,
  showCompanyName: false,
  customText: "",
  cardPreset: "8x4",
  cardWidthCm: 8,
  cardHeightCm: 4,
  qrSizeCm: 3,
  paddingCm: 0.28,
  gapCm: 0.3,
  borderStyle: "dashed",
  borderRadiusCm: 0.18,
  contentRotation: "0",
  backgroundColor: "#ffffff",
  copies: 1,
  pageSize: "8cm 4cm",
};

const PRINT_PRESETS = {
  "8x4": {
    cardPreset: "8x4",
    cardWidthCm: 8,
    cardHeightCm: 4,
    qrSizeCm: 3,
    paddingCm: 0.28,
    gapCm: 0.3,
    logoPosition: "right",
    logoSizeCm: 0.9,
  },
  square: {
    cardPreset: "square",
    cardWidthCm: 5,
    cardHeightCm: 5,
    qrSizeCm: 3.2,
    paddingCm: 0.35,
    gapCm: 0.35,
    logoPosition: "top",
    logoSizeCm: 0.8,
  },
};

const PREVIEW_PX_PER_CM = 37.8;
const PRINT_IMAGE_TIMEOUT_MS = 8000;
const MAX_PRINT_CARDS = 120;

const clamp = (value, min, max) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return min;
  return Math.min(max, Math.max(min, numberValue));
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getBorderValue = (design) => {
  if (design.borderStyle === "none") return "none";
  return `1px ${design.borderStyle} #c7d7ea`;
};

const cmValue = (value) => `${Number(value).toFixed(2)}cm`;

const previewPx = (value) =>
  `${Math.round(Number(value) * PREVIEW_PX_PER_CM)}px`;

const getContentSize = (design) => ({
  widthCm: Math.max(1, Number(design.cardWidthCm) - Number(design.paddingCm) * 2),
  heightCm: Math.max(
    1,
    Number(design.cardHeightCm) - Number(design.paddingCm) * 2,
  ),
});

const isQuarterTurn = (rotation) => rotation === "90" || rotation === "270";

const waitForPrintImages = (printDocument) => {
  const images = Array.from(printDocument.images || []);
  const pendingImages = images.filter((image) => !image.complete);

  if (!pendingImages.length) return Promise.resolve();

  return Promise.race([
    Promise.all(
      pendingImages.map(
        (image) =>
          new Promise((resolve) => {
            image.onload = resolve;
            image.onerror = resolve;
          }),
      ),
    ),
    new Promise((resolve) => setTimeout(resolve, PRINT_IMAGE_TIMEOUT_MS)),
  ]);
};

const getLogoMarkup = (design, position) => {
  if (!design.showLogo || !design.logoSrc || design.logoPosition !== position) {
    return "";
  }

  return `
    <div class="qr-print-logo-wrap qr-print-logo-${position}">
      <img class="qr-print-logo-image qr-print-logo-shape-${design.logoShape}" src="${escapeHtml(
        design.logoSrc,
      )}" alt="Logo" />
    </div>
  `;
};

const buildTextRows = (item, design) =>
  [
    design.showLabel
      ? `<div class="qr-print-card-label">${escapeHtml(item.label || "QR: XXXX")}</div>`
      : "",
    design.showProductName && item.productName
      ? `<div class="qr-print-card-meta">${escapeHtml(item.productName)}</div>`
      : "",
    design.showCompanyName && item.companyName
      ? `<div class="qr-print-card-meta">${escapeHtml(item.companyName)}</div>`
      : "",
    design.customText
      ? `<div class="qr-print-card-custom-text">${escapeHtml(
          design.customText,
        )}</div>`
      : "",
  ].join("");

export const buildQrPrintHtml = (qrItems = [], printDesign = {}) => {
  const design = { ...DEFAULT_PRINT_DESIGN, ...printDesign };
  const isCardPage = design.pageSize === "8cm 4cm";
  const contentSize = getContentSize(design);
  const rotated = isQuarterTurn(design.contentRotation);
  const copies = clamp(design.copies, 1, 20);
  const printableItems = qrItems
    .filter((item) => item?.imageSrc)
    .flatMap((item) => Array.from({ length: copies }, () => item));

  const cards = printableItems
    .map((item) => {
      const textRows = buildTextRows(item, design);
      const centerLogo = getLogoMarkup(design, "center");
      const outsideTopLogo = getLogoMarkup(design, "top");
      const outsideBottomLogo = getLogoMarkup(design, "bottom");
      const sideLogo =
        getLogoMarkup(design, "left") || getLogoMarkup(design, "right");
      const isSideLayout =
        design.logoPosition === "left" || design.logoPosition === "right";

      if (isSideLayout) {
        return `
          <div class="qr-print-card qr-print-layout-${design.logoPosition}">
            <div class="qr-print-card-content qr-print-layout-${design.logoPosition} qr-print-rotation-${design.contentRotation}">
              ${design.logoPosition === "left" ? `<div class="qr-print-side-content">${sideLogo}${textRows}</div>` : ""}
              <div class="qr-print-qr-frame">
                <img class="qr-print-card-image" src="${escapeHtml(
                  item.imageSrc,
                )}" alt="${escapeHtml(item.label || "Product QR Code")}" />
              </div>
              ${design.logoPosition === "right" ? `<div class="qr-print-side-content">${sideLogo}${textRows}</div>` : ""}
            </div>
          </div>
        `;
      }

      return `
        <div class="qr-print-card qr-print-layout-${design.logoPosition}">
          <div class="qr-print-card-content qr-print-layout-${design.logoPosition} qr-print-rotation-${design.contentRotation}">
            <div class="qr-print-card-main">
              ${outsideTopLogo}
              <div class="qr-print-qr-frame">
                <img class="qr-print-card-image" src="${escapeHtml(
                  item.imageSrc,
                )}" alt="${escapeHtml(item.label || "Product QR Code")}" />
                ${centerLogo}
              </div>
              ${outsideBottomLogo}
              ${textRows ? `<div class="qr-print-card-copy">${textRows}</div>` : ""}
            </div>
          </div>
        </div>
      `;
    })
    .join("");

  return `
    <html>
      <head>
        <title>Print QR Codes</title>
        <style>
          @page {
            size: ${design.pageSize};
            margin: ${isCardPage ? "0" : "12mm"};
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: ${isCardPage ? "0" : "24px"};
            font-family: Inter, Arial, sans-serif;
            background: #ffffff;
            color: #1f2937;
          }

          .qr-print-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(${cmValue(
              design.cardWidthCm,
            )}, ${cmValue(design.cardWidthCm)}));
            gap: ${isCardPage ? "0" : cmValue(design.gapCm)};
            justify-content: center;
            align-items: start;
          }

          .qr-print-card {
            width: ${cmValue(design.cardWidthCm)};
            height: ${cmValue(design.cardHeightCm)};
            padding: ${cmValue(design.paddingCm)};
            border: ${getBorderValue(design)};
            border-radius: ${cmValue(design.borderRadiusCm)};
            background: ${design.backgroundColor};
            display: flex;
            align-items: center;
            justify-content: center;
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: ${isCardPage ? "always" : "auto"};
            overflow: hidden;
          }

          .qr-print-card-content {
            width: ${cmValue(rotated ? contentSize.heightCm : contentSize.widthCm)};
            height: ${cmValue(rotated ? contentSize.widthCm : contentSize.heightCm)};
            display: flex;
            align-items: center;
            justify-content: center;
            transform-origin: center;
          }

          .qr-print-rotation-90 {
            transform: rotate(90deg);
          }

          .qr-print-rotation-180 {
            transform: rotate(180deg);
          }

          .qr-print-rotation-270 {
            transform: rotate(270deg);
          }

          .qr-print-layout-left,
          .qr-print-layout-right {
            gap: ${cmValue(design.gapCm)};
          }

          .qr-print-card-main {
            display: flex;
            flex-direction: column;
            align-items: center;
            min-width: 0;
          }

          .qr-print-side-content {
            min-width: 0;
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: ${cmValue(design.gapCm)};
            text-align: center;
          }

          .qr-print-qr-frame {
            position: relative;
            width: ${cmValue(design.qrSizeCm)};
            height: ${cmValue(design.qrSizeCm)};
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            padding: 0.12cm;
          }

          .qr-print-card-image {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }

          .qr-print-logo-wrap {
            width: ${cmValue(design.logoSizeCm)};
            height: ${cmValue(design.logoSizeCm)};
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            overflow: hidden;
            flex: 0 0 auto;
          }

          .qr-print-logo-top {
            margin-bottom: ${cmValue(design.gapCm)};
          }

          .qr-print-logo-bottom {
            margin-top: ${cmValue(design.gapCm)};
          }

          .qr-print-logo-center {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            padding: 0.08cm;
            box-shadow: 0 0 0 0.08cm #ffffff;
          }

          .qr-print-logo-image {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }

          .qr-print-logo-shape-circle {
            border-radius: 999px;
          }

          .qr-print-logo-shape-rounded {
            border-radius: 10px;
          }

          .qr-print-logo-shape-square {
            border-radius: 0;
          }

          .qr-print-card-copy {
            margin-top: ${cmValue(design.gapCm)};
            display: flex;
            flex-direction: column;
            gap: 0.08cm;
            text-align: center;
            width: 100%;
          }

          .qr-print-card-label {
            color: #344054;
            font-size: 14px;
            font-weight: 700;
            line-height: 1.25;
            overflow-wrap: anywhere;
          }

          .qr-print-card-meta {
            color: #667085;
            font-size: 12px;
            font-weight: 500;
            line-height: 1.25;
            overflow-wrap: anywhere;
          }

          .qr-print-card-custom-text {
            color: #101828;
            font-size: 12px;
            font-weight: 600;
            line-height: 1.25;
            overflow-wrap: anywhere;
          }

          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="qr-print-grid">${cards}</div>
      </body>
    </html>
  `;
};

function QrLabelPreview({ item, design }) {
  const contentSize = getContentSize(design);
  const rotated = isQuarterTurn(design.contentRotation);
  const cardStyle = {
    width: previewPx(design.cardWidthCm),
    height: previewPx(design.cardHeightCm),
    padding: previewPx(design.paddingCm),
    border: getBorderValue(design),
    borderRadius: previewPx(design.borderRadiusCm),
    background: design.backgroundColor,
  };
  const logoStyle = {
    width: previewPx(design.logoSizeCm),
    height: previewPx(design.logoSizeCm),
  };
  const qrStyle = {
    width: previewPx(design.qrSizeCm),
    height: previewPx(design.qrSizeCm),
  };
  const contentStyle = {
    width: previewPx(rotated ? contentSize.heightCm : contentSize.widthCm),
    height: previewPx(rotated ? contentSize.widthCm : contentSize.heightCm),
  };
  const showLogo = design.showLogo && design.logoSrc;
  const isSideLayout =
    design.logoPosition === "left" || design.logoPosition === "right";
  const logo = showLogo ? (
    <div
      className={`qr-print-designer-logo qr-print-designer-logo-${design.logoPosition} qr-print-designer-logo-shape-${design.logoShape}`}
      style={logoStyle}
    >
      <img src={design.logoSrc} alt="Logo" />
    </div>
  ) : null;
  const textRows =
    design.showLabel ||
    design.showProductName ||
    design.showCompanyName ||
    design.customText ? (
      <div className="qr-print-designer-preview-copy">
        {design.showLabel && (
          <div className="qr-print-designer-preview-label">
            {item?.label || "QR: XXXX"}
          </div>
        )}
        {design.showProductName && item?.productName && (
          <div className="qr-print-designer-preview-meta">
            {item.productName}
          </div>
        )}
        {design.showCompanyName && item?.companyName && (
          <div className="qr-print-designer-preview-meta">
            {item.companyName}
          </div>
        )}
        {design.customText && (
          <div className="qr-print-designer-preview-custom-text">
            {design.customText}
          </div>
        )}
      </div>
    ) : null;

  if (isSideLayout) {
    return (
      <div
        className="qr-print-designer-preview-card"
        style={cardStyle}
      >
        <div
          className={`qr-print-designer-card-content qr-print-designer-preview-${design.logoPosition} qr-print-designer-rotation-${design.contentRotation}`}
          style={contentStyle}
        >
          {design.logoPosition === "left" ? (
            <div className="qr-print-designer-side-content">
              {logo}
              {textRows}
            </div>
          ) : null}
          <div className="qr-print-designer-qr-frame" style={qrStyle}>
            {item?.imageSrc ? (
              <img src={item.imageSrc} alt={item.label} />
            ) : null}
          </div>
          {design.logoPosition === "right" ? (
            <div className="qr-print-designer-side-content">
              {logo}
              {textRows}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className="qr-print-designer-preview-card"
      style={cardStyle}
    >
      <div
        className={`qr-print-designer-card-content qr-print-designer-preview-${design.logoPosition} qr-print-designer-rotation-${design.contentRotation}`}
        style={contentStyle}
      >
        <div className="qr-print-designer-preview-main">
          {design.logoPosition === "top" ? logo : null}
          <div className="qr-print-designer-qr-frame" style={qrStyle}>
            {item?.imageSrc ? (
              <img src={item.imageSrc} alt={item.label} />
            ) : null}
            {design.logoPosition === "center" ? logo : null}
          </div>
          {design.logoPosition === "bottom" ? logo : null}
          {textRows}
        </div>
      </div>
    </div>
  );
}

function QrPrintDesigner({
  open,
  qrItems,
  title,
  subtitle,
  onClose,
  onPrinted,
}) {
  const [design, setDesign] = useState(DEFAULT_PRINT_DESIGN);
  const [logoName, setLogoName] = useState("");
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);
  const [printError, setPrintError] = useState("");
  const logoObjectUrlRef = useRef("");
  const previewItems = useMemo(
    () =>
      Array.isArray(qrItems) ? qrItems.filter((item) => item?.imageSrc) : [],
    [qrItems],
  );
  const printCardCount = previewItems.length * clamp(design.copies, 1, 20);

  const clearTemporaryState = () => {
    if (logoObjectUrlRef.current && window.URL?.revokeObjectURL) {
      window.URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = "";
    }

    setDesign(DEFAULT_PRINT_DESIGN);
    setLogoName("");
    setIsPreparingPrint(false);
    setPrintError("");
  };

  useEffect(
    () => () => {
      if (logoObjectUrlRef.current && window.URL?.revokeObjectURL) {
        const logoObjectUrl = logoObjectUrlRef.current;
        setTimeout(() => window.URL.revokeObjectURL(logoObjectUrl), 30000);
        logoObjectUrlRef.current = "";
      }
    },
    [],
  );

  if (!open) return null;

  const handleClose = () => {
    clearTemporaryState();
    onClose?.();
  };

  const updateDesign = (field, value) => {
    setPrintError("");
    setDesign((prev) => ({ ...prev, [field]: value }));
  };

  const applyCardPreset = (preset) => {
    if (preset === "custom") {
      updateDesign("cardPreset", "custom");
      return;
    }

    setDesign((prev) => ({
      ...prev,
      ...PRINT_PRESETS[preset],
    }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (logoObjectUrlRef.current && window.URL?.revokeObjectURL) {
      window.URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = "";
    }

    if (window.URL?.createObjectURL) {
      const logoObjectUrl = window.URL.createObjectURL(file);
      logoObjectUrlRef.current = logoObjectUrl;
      updateDesign("logoSrc", logoObjectUrl);
      updateDesign("showLogo", true);
      setLogoName(file.name);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      updateDesign("logoSrc", String(reader.result || ""));
      updateDesign("showLogo", true);
      setLogoName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handlePrint = async () => {
    if (!previewItems.length || isPreparingPrint) return;

    if (printCardCount > MAX_PRINT_CARDS) {
      setPrintError(
        `This print job has ${printCardCount} labels. Please reduce copies or print in batches of ${MAX_PRINT_CARDS} labels or fewer.`,
      );
      return;
    }

    setPrintError("");
    setIsPreparingPrint(true);

    const printWindow = window.open("", "_blank", "width=1000,height=800");
    if (!printWindow) {
      setIsPreparingPrint(false);
      return;
    }

    try {
      printWindow.document.write(buildQrPrintHtml(previewItems, design));
      printWindow.document.close();
      await waitForPrintImages(printWindow.document);
      printWindow.focus();
      printWindow.print();
      clearTemporaryState();
      onPrinted?.();
    } finally {
      setIsPreparingPrint(false);
    }
  };

  const visiblePreviewItems = previewItems.slice(0, 6);

  return createPortal(
    <div className="qr-print-designer-overlay" onClick={handleClose}>
      <div
        className="qr-print-designer-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="qr-print-designer-close"
          onClick={handleClose}
          aria-label="Close print designer"
        >
          <XIcon stroke="#424242" strokeWidth="2" />
        </button>

        <div className="qr-print-designer-header">
          <div>
            <div className="qr-print-designer-title">
              {title || "Customize QR Print"}
            </div>
            <div className="qr-print-designer-subtitle">
              {subtitle || "Adjust the printable label before sending it out."}
            </div>
          </div>
          <div className="qr-print-designer-count">
            {previewItems.length} QR{previewItems.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="qr-print-designer-body">
          <div className="qr-print-designer-panel">
            <div className="qr-print-designer-section-title">Logo</div>
            <label className="qr-print-designer-upload">
              <input type="file" accept="image/*" onChange={handleLogoChange} />
              <Uploadbutton />
              <span>{logoName || "Upload Logo"}</span>
            </label>

            <label className="qr-print-designer-check">
              <input
                type="checkbox"
                checked={design.showLogo}
                onChange={(event) =>
                  updateDesign("showLogo", event.target.checked)
                }
              />
              <span>Show logo</span>
            </label>

            <div className="qr-print-designer-field">
              <span>Logo position</span>
              <CustomDropdown
                rightIcon={<ArrowDownIcon />}
                value={design.logoPosition}
                onChange={(value) => updateDesign("logoPosition", value)}
                placeholder="Select position"
                options={[
                  { label: "Top", value: "top" },
                  { label: "Bottom", value: "bottom" },
                  { label: "Left", value: "left" },
                  { label: "Right", value: "right" },
                  { label: "Center", value: "center" },
                ]}
                className="qr-print-designer-dropdown"
                menuClassName="qr-print-designer-dropdown-menu"
                optionClassName="qr-print-designer-dropdown-option"
              />
            </div>

            <div className="qr-print-designer-field">
              <span>Logo shape</span>
              <CustomDropdown
                rightIcon={<ArrowDownIcon />}
                value={design.logoShape}
                onChange={(value) => updateDesign("logoShape", value)}
                placeholder="Select shape"
                options={[
                  { label: "Rounded", value: "rounded" },
                  { label: "Circle", value: "circle" },
                  { label: "Square", value: "square" },
                ]}
                className="qr-print-designer-dropdown"
                menuClassName="qr-print-designer-dropdown-menu"
                optionClassName="qr-print-designer-dropdown-option"
              />
            </div>

            <label className="qr-print-designer-range">
              <span>Logo size</span>
              <input
                type="range"
                min="0.4"
                max="2"
                step="0.1"
                value={design.logoSizeCm}
                onChange={(event) =>
                  updateDesign("logoSizeCm", Number(event.target.value))
                }
              />
              <strong>{design.logoSizeCm}cm</strong>
            </label>

            <div className="qr-print-designer-section-title">Label</div>
            {/* <label className="qr-print-designer-check">
              <input
                type="checkbox"
                checked={design.showLabel}
                onChange={(event) =>
                  updateDesign("showLabel", event.target.checked)
                }
              />
              <span>QR label</span>
            </label> */}
            <label className="qr-print-designer-check">
              <input
                type="checkbox"
                checked={design.showProductName}
                onChange={(event) =>
                  updateDesign("showProductName", event.target.checked)
                }
              />
              <span>Product name</span>
            </label>
            <label className="qr-print-designer-check">
              <input
                type="checkbox"
                checked={design.showCompanyName}
                onChange={(event) =>
                  updateDesign("showCompanyName", event.target.checked)
                }
              />
              <span>Company name</span>
            </label>
            <label className="qr-print-designer-text-field">
              <span>Custom text</span>
              <input
                type="text"
                style={{ width: "90%" }}
                value={design.customText}
                placeholder="Add text to printed label"
                onChange={(event) =>
                  updateDesign("customText", event.target.value)
                }
              />
            </label>

            <div className="qr-print-designer-section-title">Layout</div>
            <div className="qr-print-designer-field">
              <span>Card preset</span>
              <CustomDropdown
                rightIcon={<ArrowDownIcon />}
                value={design.cardPreset}
                onChange={applyCardPreset}
                placeholder="Select preset"
                options={[
                  { label: "8cm x 4cm Card", value: "8x4" },
                  { label: "5cm x 5cm Square", value: "square" },
                  { label: "Custom", value: "custom" },
                ]}
                className="qr-print-designer-dropdown"
                menuClassName="qr-print-designer-dropdown-menu"
                optionClassName="qr-print-designer-dropdown-option"
              />
            </div>

            <div className="qr-print-designer-field">
              <span>Content rotation</span>
              <CustomDropdown
                rightIcon={<ArrowDownIcon />}
                value={design.contentRotation}
                onChange={(value) => updateDesign("contentRotation", value)}
                placeholder="Select rotation"
                options={[
                  { label: "No rotation", value: "0" },
                  { label: "Rotate 90", value: "90" },
                  { label: "Rotate 180", value: "180" },
                  { label: "Rotate 270", value: "270" },
                ]}
                className="qr-print-designer-dropdown"
                menuClassName="qr-print-designer-dropdown-menu"
                optionClassName="qr-print-designer-dropdown-option"
              />
            </div>

            <div className="qr-print-designer-inline-fields">
              <label className="qr-print-designer-small-field">
                <span>Width cm</span>
                <input
                  style={{ width: "50%" }}
                  type="number"
                  min="2"
                  max="20"
                  step="0.1"
                  value={design.cardWidthCm}
                  onChange={(event) =>
                    setDesign((prev) => ({
                      ...prev,
                      cardPreset: "custom",
                      cardWidthCm: clamp(event.target.value, 2, 20),
                    }))
                  }
                />
              </label>
              <label className="qr-print-designer-small-field">
                <span>Height cm</span>
                <input
                  type="number"
                  style={{ width: "50%" }}
                  min="2"
                  max="20"
                  step="0.1"
                  value={design.cardHeightCm}
                  onChange={(event) =>
                    setDesign((prev) => ({
                      ...prev,
                      cardPreset: "custom",
                      cardHeightCm: clamp(event.target.value, 2, 20),
                    }))
                  }
                />
              </label>
            </div>

            <label className="qr-print-designer-range">
              <span>QR size</span>
              <input
                type="range"
                min="1.5"
                max="3.4"
                step="0.1"
                value={design.qrSizeCm}
                onChange={(event) =>
                  updateDesign("qrSizeCm", Number(event.target.value))
                }
              />
              <strong>{design.qrSizeCm}cm</strong>
            </label>
            <label className="qr-print-designer-range">
              <span>Padding</span>
              <input
                type="range"
                min="0.1"
                max="0.8"
                step="0.05"
                value={design.paddingCm}
                onChange={(event) =>
                  updateDesign("paddingCm", Number(event.target.value))
                }
              />
              <strong>{design.paddingCm}cm</strong>
            </label>
            <label className="qr-print-designer-range">
              <span>Gap</span>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={design.gapCm}
                onChange={(event) =>
                  updateDesign("gapCm", Number(event.target.value))
                }
              />
              <strong>{design.gapCm}cm</strong>
            </label>
            <label className="qr-print-designer-range">
              <span>Radius</span>
              <input
                type="range"
                min="0"
                max="0.6"
                step="0.05"
                value={design.borderRadiusCm}
                onChange={(event) =>
                  updateDesign("borderRadiusCm", Number(event.target.value))
                }
              />
              <strong>{design.borderRadiusCm}cm</strong>
            </label>

            <div className="qr-print-designer-field">
              <span>Border</span>
              <CustomDropdown
                rightIcon={<ArrowDownIcon />}
                value={design.borderStyle}
                onChange={(value) => updateDesign("borderStyle", value)}
                placeholder="Select border"
                options={[
                  { label: "Dashed", value: "dashed" },
                  { label: "Solid", value: "solid" },
                  { label: "None", value: "none" },
                ]}
                className="qr-print-designer-dropdown"
                menuClassName="qr-print-designer-dropdown-menu"
                optionClassName="qr-print-designer-dropdown-option"
              />
            </div>

            <div className="qr-print-designer-field">
              <span>Page</span>
              <CustomDropdown
                rightIcon={<ArrowDownIcon />}
                value={design.pageSize}
                onChange={(value) => updateDesign("pageSize", value)}
                placeholder="Select page"
                options={[
                  { label: "8cm x 4cm Card", value: "8cm 4cm" },
                  { label: "A4", value: "A4" },
                  { label: "Letter", value: "Letter" },
                ]}
                className="qr-print-designer-dropdown"
                menuClassName="qr-print-designer-dropdown-menu"
                optionClassName="qr-print-designer-dropdown-option"
              />
            </div>

            <div className="qr-print-designer-inline-fields">
              <label className="qr-print-designer-small-field">
                <span>Copies</span>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={design.copies}
                  onChange={(event) =>
                    updateDesign("copies", clamp(event.target.value, 1, 20))
                  }
                />
              </label>
              {/* <label className="qr-print-designer-small-field">
                <span>Background</span>
                <input
                  type="color"
                  value={design.backgroundColor}
                  onChange={(event) =>
                    updateDesign("backgroundColor", event.target.value)
                  }
                />
              </label> */}
            </div>
          </div>

          <div className="qr-print-designer-preview">
            <div className="qr-print-designer-preview-toolbar">
              <div className="qr-print-designer-section-title">Preview</div>
              <button
                type="button"
                className="qr-print-designer-reset"
                onClick={() => {
                  setDesign(DEFAULT_PRINT_DESIGN);
                  setLogoName("");
                }}
              >
                Reset
              </button>
            </div>
            <div
              className="qr-print-designer-preview-grid"
              style={{ gap: previewPx(Math.min(design.gapCm, 0.75)) }}
            >
              {visiblePreviewItems.length ? (
                visiblePreviewItems.map((item, index) => (
                  <QrLabelPreview
                    key={`${item.qrCodeId || item.label || index}-${index}`}
                    item={item}
                    design={design}
                  />
                ))
              ) : (
                <div className="qr-print-designer-empty">No QR selected</div>
              )}
            </div>
            {previewItems.length > visiblePreviewItems.length ? (
              <div className="qr-print-designer-more">
                +{previewItems.length - visiblePreviewItems.length} more
              </div>
            ) : null}
          </div>
        </div>

        <div className="qr-print-designer-footer">
          {printError ? (
            <div className="qr-print-designer-warning">{printError}</div>
          ) : null}
          <button
            type="button"
            className="qr-print-designer-secondary"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="qr-print-designer-primary"
            disabled={!previewItems.length || isPreparingPrint}
            onClick={handlePrint}
          >
            {isPreparingPrint ? "Preparing..." : "Print"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default QrPrintDesigner;
